import useSWR from "swr";
import { useCreateStripeAdminAxiosInstance } from "../../Auth/useCreateStripeAdminAxiosInstance.ts";
import { useCreateHeyLightAdminAxiosInstance } from "../../Auth/useCreateHeyLightAdminAxiosInstance.ts";
import {
  NormasTeachingItemType,
  StripeOrderDTO,
} from "../../Stripe/StripeOrderDTO.ts";
import { HeyLightOrderDTO } from "../../HeyLight/heyLightOrderDTO.ts";
import { PurchasedItemSnapshotDTO } from "../../ProductService/PurchasedItem/PurchasedItemSnapshotDTO.ts";

export type WithdrawalOrderSummary = {
  gateway: "Stripe" | "HeyLight";
  orderId: string;
  productId: string;
  productType: NormasTeachingItemType;
  productSnapshot?: PurchasedItemSnapshotDTO;
  amountInCent: { amount: number; currency: string };
  status: string;
  createdAt: string;
  detailPath: string;
};

export function extractGatewayOrderId(
  planOrderId: string | undefined,
): string | undefined {
  if (!planOrderId) return undefined;
  return planOrderId.match(/-order:(.+)$/)?.[1] ?? planOrderId;
}

function fromStripeOrder(order: StripeOrderDTO): WithdrawalOrderSummary {
  return {
    gateway: "Stripe",
    orderId: order._id,
    productId: order.normasTeachingProductId,
    productType: order.productType,
    productSnapshot: order.productSnapshot,
    amountInCent: order.amountInCent,
    status: order.status,
    createdAt: order.createdAt,
    detailPath: `/stripe/order/${order._id}`,
  };
}

function fromHeyLightOrder(order: HeyLightOrderDTO): WithdrawalOrderSummary {
  return {
    gateway: "HeyLight",
    orderId: order._id,
    productId: order.productId,
    productType: order.productType,
    productSnapshot: order.productSnapshot,
    amountInCent: order.amountInCent,
    status: order.heyLightInformation.heyLightOrderStatus,
    createdAt: order.createdAt,
    detailPath: `/heylight/order/${order._id}`,
  };
}

export function useWithdrawalOrderSummary(planOrderId: string | undefined) {
  const { createAxiosInstance: createStripeAxiosInstance } =
    useCreateStripeAdminAxiosInstance();
  const { createAxiosInstance: createHeyLightAxiosInstance } =
    useCreateHeyLightAdminAxiosInstance();

  const gatewayOrderId = extractGatewayOrderId(planOrderId);

  const { data, isLoading } = useSWR(
    gatewayOrderId ? ["withdrawal-order-summary", gatewayOrderId] : null,
    async (): Promise<WithdrawalOrderSummary | null> => {
      try {
        const { data } = await createStripeAxiosInstance().get<StripeOrderDTO>(
          `/admin/orders/${gatewayOrderId}`,
        );
        return fromStripeOrder(data);
      } catch {
        try {
          const { data } =
            await createHeyLightAxiosInstance().get<HeyLightOrderDTO>(
              `/admin/orders/${gatewayOrderId}`,
            );
          return fromHeyLightOrder(data);
        } catch {
          return null;
        }
      }
    },
  );

  return {
    order: data ?? null,
    gatewayOrderId,
    isLoading,
  };
}
